import React, { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, ClipboardList, User, Mail, Phone } from "lucide-react";

const TYPE_LABELS = {
  menopause: "Perimenopause & Menopause",
  fertility: "Fertility",
  general: "General Gynecological & Preventive",
  comprehensive: "Comprehensive Women's Health",
};
const LANG_LABELS = { de: "DE", en: "EN", ar: "AR" };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export const QuestionnairesManager = ({ submissions }) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return submissions.filter((s) => {
      if (filter !== "all" && s.type !== filter) return false;
      if (!term) return true;
      return [s.patient?.name, s.patient?.email].some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [submissions, q, filter]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-deep-ink/40" />
          <Input data-testid="qn-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient, email…" className="bg-warm-ivory ps-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-64 bg-warm-ivory" data-testid="qn-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All questionnaires</SelectItem>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 overflow-hidden rounded-3xl bg-warm-ivory pb-2">
        {filtered.map((s) => (
          <button key={s.id} onClick={() => setSel(s)} data-testid={`qn-row-${s.id}`}
            className="flex w-full items-center gap-4 border-t border-border/60 px-4 py-3 text-start transition-colors hover:bg-sand/60">
            <div className="w-44 flex-shrink-0">
              <p className="truncate text-sm font-medium text-deep-ink">{s.patient?.name || "—"}</p>
              <p className="truncate text-xs text-deep-ink/50">{s.patient?.email}</p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 truncate text-sm text-deep-ink"><ClipboardList className="h-3.5 w-3.5 text-champagne-gold" />{TYPE_LABELS[s.type] || s.type}</p>
              <p className="text-xs text-deep-ink/40">{fmtDate(s.created_at)}</p>
            </div>
            <Badge className="flex-shrink-0 bg-muted-sage/30 text-deep-ink">{LANG_LABELS[s.language] || s.language}</Badge>
          </button>
        ))}
        {filtered.length === 0 && <p className="p-8 text-center text-deep-ink/50">No questionnaire submissions yet.</p>}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="qn-detail-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-deep-ink">
                  <ClipboardList className="h-5 w-5 text-champagne-gold" />{TYPE_LABELS[sel.type] || sel.type}
                </DialogTitle>
                <DialogDescription className="text-sm text-deep-ink/60">
                  Submitted {fmtDate(sel.created_at)} · Language: {LANG_LABELS[sel.language] || sel.language}
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-2xl bg-sand p-4 text-sm">
                <p className="flex items-center gap-2 font-medium text-deep-ink"><User className="h-4 w-4 text-champagne-gold" />{sel.patient?.name}</p>
                <p className="mt-1 flex items-center gap-2 text-deep-ink/70"><Mail className="h-3.5 w-3.5" />{sel.patient?.email}</p>
                {sel.patient?.phone && <p className="mt-1 flex items-center gap-2 text-deep-ink/70"><Phone className="h-3.5 w-3.5" />{sel.patient.phone}</p>}
              </div>
              <div className="mt-2 space-y-3">
                {Object.entries(sel.answers || {}).map(([key, value]) => (
                  <div key={key} className="border-b border-border/60 pb-2">
                    <p className="text-xs uppercase tracking-editorial text-deep-ink/40">{key}</p>
                    <p className="mt-0.5 text-sm text-deep-ink">
                      {Array.isArray(value) ? (value.length ? value.join(", ") : "—") : (value || "—").toString()}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
